import React, { useState, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import DonutChartComponent from '../../components/charts/DonutChartComponent';
import TagBadge from '../../components/TagBadge';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import aircallService, { TopicData, DashboardStats, CallSummary, Team } from '../../services/aircall.service';

const Aircall: React.FC = () => {
  const [topicsData, setTopicsData] = useState<TopicData[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [totalTopicsCount, setTotalTopicsCount] = useState<number>(0);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [callSummaries, setCallSummaries] = useState<CallSummary[]>([]);
  const [loadingCallSummaries, setLoadingCallSummaries] = useState(true);
  const [selectedSentiment, setSelectedSentiment] = useState<'negative' | 'positive' | 'neutral' | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [topicSentiments, setTopicSentiments] = useState<{ positive: number; neutral: number; negative: number } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Team filter state
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Convert selected team IDs to numbers for API calls
  const teamIdsForApi = selectedTeamIds.length > 0 ? selectedTeamIds.map(id => parseInt(id)) : undefined;

  // Fetch teams on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const data = await aircallService.getTeams();
        setTeams(data);
      } catch (error) {
        console.error('Failed to fetch teams:', error);
      }
    };

    fetchTeams();
  }, []);

  // Fetch dashboard stats when team filter changes
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoadingStats(true);
        const stats = await aircallService.getDashboardStats(teamIdsForApi);
        setDashboardStats(stats);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [selectedTeamIds]);

  // Fetch top topics and total count when team filter changes
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoadingTopics(true);
        const [data, totalCount] = await Promise.all([
          aircallService.getTopTopics(40, teamIdsForApi),
          aircallService.getTopicsCount(teamIdsForApi)
        ]);

        setTotalTopicsCount(totalCount);

        if (data && data.length > 0) {
          // Capitalize topic names for display
          const formattedData = data.map(topic => ({
            ...topic,
            name: topic.name
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
          }));
          setTopicsData(formattedData);
        } else {
          console.log('No topics data returned from API');
          setTopicsData([]);
        }
      } catch (error) {
        console.error('Failed to fetch topics:', error);
        setTopicsData([]);
      } finally {
        setLoadingTopics(false);
      }
    };

    fetchTopics();
  }, [selectedTeamIds]);

  // Fetch topic sentiments when a topic is selected or team filter changes
  useEffect(() => {
    const fetchTopicSentiments = async () => {
      if (!selectedTopic) {
        setTopicSentiments(null);
        return;
      }

      try {
        const sentiments = await aircallService.getTopicSentiments(selectedTopic, teamIdsForApi);
        setTopicSentiments(sentiments);
      } catch (error) {
        console.error('Failed to fetch topic sentiments:', error);
        setTopicSentiments(null);
      }
    };

    fetchTopicSentiments();
  }, [selectedTopic, selectedTeamIds]);

  // Fetch call summaries based on selected sentiment, topic, and team filter
  useEffect(() => {
    const fetchCallSummaries = async () => {
      try {
        setLoadingCallSummaries(true);
        const data = await aircallService.getCallSummaries(
          selectedSentiment || undefined,
          selectedTopic || undefined,
          10000, // High limit to get all summaries
          teamIdsForApi
        );
        setCallSummaries(data);
        setCurrentPage(1); // Reset to first page when filters change
      } catch (error) {
        console.error('Failed to fetch call summaries:', error);
        setCallSummaries([]);
      } finally {
        setLoadingCallSummaries(false);
      }
    };

    fetchCallSummaries();
  }, [selectedSentiment, selectedTopic, selectedTeamIds]);

  // Calculate pagination
  const totalPages = Math.ceil(callSummaries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSummaries = callSummaries.slice(startIndex, endIndex);

  // Format sentiment data for donut chart
  // Use topic-specific sentiments if a topic is selected, otherwise use overall stats
  const activeSentiments = selectedTopic && topicSentiments ? topicSentiments : dashboardStats?.sentiments;
  const sentimentData = activeSentiments ? [
    { name: 'Positive', value: activeSentiments.positive },
    { name: 'Neutral', value: activeSentiments.neutral },
    { name: 'Negative', value: activeSentiments.negative },
  ] : [];

  const totalCalls = sentimentData.reduce((sum, item) => sum + item.value, 0);

  // Format duration (seconds to MM:SS)
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date to "Nov 18 2:34 PM"
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${month} ${day} ${time}`;
  };

  // Format last update date - converts from server timezone to CST and formats
  const formatLastUpdate = (dateString: string): string => {
    const date = new Date(dateString);
    // Format in CST timezone
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Chicago'
    };
    return date.toLocaleString('en-US', options);
  };

  // Convert teams to MultiSelect options
  const teamOptions: MultiSelectOption[] = teams.map(team => ({
    value: team.team_id.toString(),
    label: team.team_name
  }));

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-heading">
          Aircall Analytics
        </h1>
        <div className="w-64">
          <MultiSelect
            options={teamOptions}
            value={selectedTeamIds}
            onChange={setSelectedTeamIds}
            placeholder="All Teams"
            enableSearch={true}
            searchPlaceholder="Search teams..."
            showClearOption={true}
            clearOptionLabel="All Teams"
          />
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Call Stats */}
        <StatCard
          title="Call Stats"
          value={dashboardStats ? dashboardStats.call_stats.total.toLocaleString() : '-'}
          secondaryValue="Total calls"
          secondaryValueColor="gray"
          showInfoButton={false}
          showViewButton={false}
          loading={loadingStats}
          headerContent={
            dashboardStats && (
              <div className="text-right text-xs text-gray-500 dark:text-gray-500">
                <div>Updates at 12:00 AM|PM CST Daily</div>
                {dashboardStats.call_stats.last_update && (
                  <div className="text-gray-600 dark:text-gray-400 mt-1">
                    Last Call Pulled: {formatLastUpdate(dashboardStats.call_stats.last_update)}
                  </div>
                )}
              </div>
            )
          }
        >
          {dashboardStats && (
            <div className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between items-center">
                <span>Today:</span>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{dashboardStats.call_stats.today_calls.toLocaleString()}</span>
                  {dashboardStats.call_stats.pct_change !== 0 && (
                    <span className={`text-xs ${dashboardStats.call_stats.pct_change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {dashboardStats.call_stats.pct_change > 0 ? '+' : ''}{dashboardStats.call_stats.pct_change}% vs yesterday
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span>This Week:</span>
                <span className="font-semibold">{dashboardStats.call_stats.this_week_calls.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>This Month:</span>
                <span className="font-semibold">{dashboardStats.call_stats.this_month_calls.toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <span>Inbound:</span>
                <span className="font-semibold">{dashboardStats.call_stats.inbound.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Outbound:</span>
                <span className="font-semibold">{dashboardStats.call_stats.outbound.toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <span>Avg Length:</span>
                <span className="font-semibold">{formatDuration(dashboardStats.call_stats.avg_duration)}</span>
              </div>
            </div>
          )}
        </StatCard>

        {/* Top Topics - spans 2 columns */}
        <div className="md:col-span-2 bg-panel rounded-lg shadow p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-heading">
              Top 40 Topics
              {totalTopicsCount > 0 && (
                <span className="text-sm font-normal text-muted">
                  {' '}of {totalTopicsCount.toLocaleString()} Topics
                </span>
              )}
            </h3>
            <p className="text-xs text-muted mt-1">
              Click a topic to filter calls{selectedTopic && ' • Click again to deselect'}
            </p>
          </div>
          {loadingTopics ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : topicsData && topicsData.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {topicsData.map((topic) => {
                const isSelected = selectedTopic === topic.name.toLowerCase();

                // Determine color based on count ranges
                let color: 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink' = 'blue';
                if (isSelected) {
                  color = 'blue';
                } else if (topic.count >= 300) {
                  color = 'red';
                } else if (topic.count >= 200) {
                  color = 'pink';
                } else if (topic.count >= 150) {
                  color = 'purple';
                } else if (topic.count >= 100) {
                  color = 'indigo';
                } else if (topic.count >= 75) {
                  color = 'blue';
                } else if (topic.count >= 50) {
                  color = 'green';
                } else {
                  color = 'yellow';
                }

                return (
                  <button
                    key={topic.name}
                    onClick={() => {
                      if (isSelected) {
                        // Deselect if already selected
                        setSelectedTopic(null);
                        setSelectedSentiment(null);
                      } else {
                        // Select topic and show all sentiments
                        setSelectedTopic(topic.name.toLowerCase());
                        setSelectedSentiment(null);
                      }
                    }}
                    className="inline-block"
                  >
                    <TagBadge
                      label={`${topic.name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')} - ${topic.count}`}
                      color={color}
                      className={isSelected ? '!bg-blue-700 dark:!bg-blue-600 !text-white !border-blue-900 dark:!border-blue-800' : ''}
                    />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted">No topics data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Sentiment Analysis Donut Chart */}
        <div>
          <div className="bg-panel rounded-lg shadow p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-heading">
                Sentiment Analysis
                {selectedTopic && (
                  <span className="text-sm font-normal text-muted">
                    {' '}for {selectedTopic.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                  </span>
                )}
              </h3>
              <p className="text-xs text-muted mt-1">
                Click a segment to show summaries
              </p>
            </div>
            <DonutChartComponent
              data={sentimentData}
              colors={['#10b981', '#f59e0b', '#ef4444']}
              centerText={totalCalls.toLocaleString()}
              height={400}
              selectedSentiment={selectedSentiment || undefined}
              onSegmentClick={(data: any) => {
                // Map segment name to sentiment type
                const sentimentMap: { [key: string]: 'negative' | 'positive' | 'neutral' } = {
                  'Positive': 'positive',
                  'Neutral': 'neutral',
                  'Negative': 'negative'
                };
                const sentiment = sentimentMap[data.name];
                if (sentiment) {
                  // Toggle selection - deselect if already selected
                  if (selectedSentiment === sentiment) {
                    setSelectedSentiment(null);
                  } else {
                    setSelectedSentiment(sentiment);
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Call Summaries Card spanning 2 columns */}
        <div className="md:col-span-2 bg-panel rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-heading mb-4">
            Call Summaries
            {selectedTopic && (
              <span className="text-sm font-normal text-muted">
                {' '}for {selectedTopic.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
              </span>
            )}
            {selectedSentiment && (
              <span className="text-sm font-normal text-muted">
                {' '}- {selectedSentiment.charAt(0).toUpperCase() + selectedSentiment.slice(1)}
              </span>
            )}
          </h3>

          {/* Scrollable list of call summaries */}
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {loadingCallSummaries ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
              </div>
            ) : callSummaries.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted">No {selectedSentiment} sentiment calls found</p>
              </div>
            ) : (
              paginatedSummaries.map((call) => {
                const sentiment = call.sentiment || selectedSentiment || 'neutral';
                const borderColor = sentiment === 'negative'
                  ? 'border-red-200 dark:border-red-900'
                  : sentiment === 'positive'
                  ? 'border-green-200 dark:border-green-900'
                  : 'border-yellow-200 dark:border-yellow-900';

                const badgeColor = sentiment === 'negative'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  : sentiment === 'positive'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';

                // Capitalize names
                const capitalizeWords = (str: string) => {
                  return str.split(' ').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  ).join(' ');
                };

                const formattedCaller = capitalizeWords(call.caller);
                const formattedAgent = call.agent_name ? capitalizeWords(call.agent_name) : 'Agent';

                // Format: User Name | Driver Name
                const displayName = `${formattedAgent} | ${formattedCaller}`;
                const directionLabel = call.direction === 'inbound' ? 'Inbound' : 'Outbound';

                return (
              <div
                key={call.id}
                className={`p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border ${borderColor}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <a
                      href={`https://assets.aircall.io/calls/${call.call_id}/recording/insights`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {displayName}
                    </a>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      call.direction === 'inbound'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                    }`}>
                      {directionLabel}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
                      {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                    </span>
                    <span className="text-xs text-muted">
                      {formatDuration(call.duration)}
                    </span>
                  </div>
                  <span className="text-xs text-muted">
                    {formatDateTime(call.date)}
                  </span>
                </div>
                {/* Topic badges */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {call.topics.map((topic, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-200 text-label dark:bg-gray-700"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-label leading-relaxed">
                  {call.summary}
                </p>
              </div>
                );
              })
            )}
          </div>

          {/* Pagination Footer */}
          {!loadingCallSummaries && callSummaries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {startIndex + 1}-{Math.min(endIndex, callSummaries.length)} of {callSummaries.length} calls
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-label rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-label rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Aircall;
